import React, { useContext, useState, useEffect } from 'react';

import { FEATURES, GOOGLE_APP_CLIENT_ID } from 'constants/index';

import scriptLoader from 'helpers/script-loader';
import generateId from 'helpers/generate-id';
import getDropboxAuthToken from 'helpers/get-dropbox-auth-token';

import withConfirm from 'hoc/withConfirm';
import withGooglePicker from 'hoc/withGooglePicker';
import withEmail from 'hoc/withEmail';

import LocaleContext from 'contexts/locale-context';

import client from 'client';

import Folder from 'assets/img/folder.svg';

import Button from 'components/button';
import Spacer from 'components/spacer';

import './index.css'

// The Client ID obtained from the Google API Console. Replace with your own Client ID.
const clientId = GOOGLE_APP_CLIENT_ID;

const loadGoogleAuth = () => new Promise((resolve, reject) => {
    // Scope to use to access user's Drive items.
    const scope = [
        'https://www.googleapis.com/auth/drive.file',
        'https://www.googleapis.com/auth/drive.readonly',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
    ];

    function onAuthApiLoad() {
        window.gapi.auth.authorize(
            {
                'client_id': clientId,
                'scope': scope,
                'immediate': false
            },
            handleAuthResult);
    }

    function handleAuthResult(authResult) {
        if (authResult && !authResult.error) {
            /**
             * Without this, trying to stringify the  object throws a
             * permission denite to access property "toJSON" on firefox
             */
            delete authResult['g-oauth-window']

            if (authResult.access_token) {
                resolve(authResult)
            } else {
                // TODO: Handle case when the token is not returned
                reject();
            }
        }
    }

    window.gapi.load('auth', {'callback': onAuthApiLoad});
})

const loadGoogleUserInfo = authResponse => {
    return client.get(`https://www.googleapis.com/oauth2/v1/userinfo?alt=json&access_token=${authResponse.access_token}`)
}

const loadDropboxUserInfo = authResponse => {
    return client.post('https://api.dropboxapi.com/2/users/get_account',
        JSON.stringify({ account_id: authResponse.account_id }),
        {
            headers: {
                Authorization: `Bearer ${authResponse.access_token}`,
                "Content-Type": "application/json"
            }
        }
    )
}

const InfoRenderer = withGooglePicker()(({ openPicker, familyName, givenName, email, folder: folderName, jsTranslations, onChangeFolder, googleAuthResponse, dropboxAccessToken, type }) => {
    const [folderData, setFolderData] = useState({ name: folderName})
    const pickerTitle = 'Select a Folder';

    const loadPicker = (googleAuthResponse, dropboxAccessToken, type) => new Promise((resolve) => {
        if (type === 'google') {
            openPicker({ googleAccessToken: googleAuthResponse.access_token, title: pickerTitle, type }, resolve)
        } else if (type === 'dropbox') {
            openPicker({ dropboxAccessToken, title: pickerTitle, type }, resolve)
        }
    });

    const onChange = () => {
        loadPicker(googleAuthResponse, dropboxAccessToken, type)
        .then(data => {
            if (data) {
                setFolderData({
                    id: data.id,
                    name: data.name,
                    path: data.path
                })

                if (onChangeFolder) {
                    onChangeFolder(
                        {
                            id: data.id,
                            name: data.name,
                            path: data.path
                        }
                    );
                }
            } 
        })
    }
    return (
        <div className='info_verification'>
            <Spacer size='xs'/>

            <div className='info_verification__email'>
                <p>{givenName}{' ' +familyName} {`(${email})`}</p>
            </div>

            <Spacer size='l'/>

            <div className='info_verification__container'>
                <div>
                    <img alt='folder icon' src={Folder}/>
                    <Spacer size='xs' horizontal/>
                    <p>{folderData.name}</p>
                </div>
                <Button
                    onClick={onChange}
                    small
                    btnType='none'
                    text={
                        <span>
                            {jsTranslations.change}
                        </span>
                    }
                />
            </div>
        </div>
    );
})



const withFeatures = () => Component => withConfirm()(withEmail()(
    ({ openConfirm, openEmail, ...props }) => {
        let folderInfo = {};
        
        const { translations } = useContext(LocaleContext);
    
        const jsTranslations = translations.js;
    
        useEffect(() => {
            scriptLoader("https://apis.google.com/js/api.js?onload=loadPicker")
        }, []);
    
        const updateFolderInfo = info => {
            folderInfo = info;
        }
        return (
            <Component
                featuresBuilder={{
                    FEATURES,
                    [FEATURES.EXPORT_TO_GOOGLE_DRIVE]: (info = {}) => new Promise(async (resolve, reject) => {
                        folderInfo = {};
                        let firstResp = info.firstResp;

                        if (!info.firstResp) {
                            firstResp = await new Promise((resolve, reject) => {
                                openConfirm(
                                    {
                                        message: jsTranslations.first_auth.replace('{STORAGE}', 'Google Drive'),
                                        message2: jsTranslations.click_confirm,
                                        title: jsTranslations.save_to_google
                                    },resolve)
                            });
                        }

                        if (firstResp === 'yes') {
                            if (window.gapi) {
                                let authResponse = info.authResponse;

                                if (!authResponse) {
                                    authResponse = await loadGoogleAuth()
                                }

                                window.gapi.auth.setToken({
                                    access_token: authResponse.access_token
                                });

                                let {
                                    familyName: family_name,
                                    givenName: given_name,
                                    email
                                } = info;

                                if (!family_name || !given_name || !email) {
                                    const res = await loadGoogleUserInfo(authResponse)
                                    family_name = res.family_name;
                                    given_name = res.given_name;
                                    email = res.email;
                                }

                                await new Promise((resolve, reject) => {
                                    openConfirm(
                                        {
                                            renderComponent: () => {
                                                return (
                                                    <InfoRenderer
                                                        familyName={family_name}
                                                        givenName={given_name}
                                                        email={email}
                                                        folder={info.name || jsTranslations.my_drive}
                                                        jsTranslations={jsTranslations}
                                                        onChangeFolder={updateFolderInfo}
                                                        googleAuthResponse={authResponse}
                                                        type='google'
                                                    />
                                                )
                                            },
                                            title: jsTranslations.save_to_google,
                                            padTitle: false
                                        }, resolve, reject
                                    )
                                });

                                resolve(formatResponse(
                                    {
                                        payload: {
                                            folder_id: folderInfo.id || null,
                                            auth_token: JSON.stringify(authResponse),
                                            email
                                        },
                                        info: {
                                            title: jsTranslations.save_to_google,
                                            description: folderInfo.name ? jsTranslations.saving_to_folder.replace('{FOLDER}', folderInfo.name).replace('{EMAIL}', email) : `${jsTranslations.saving_to_base}: ${email}`,
                                            name: folderInfo.name,
                                            email,
                                            firstResp,
                                            familyName: family_name,
                                            givenName: given_name,
                                            user_name: `${given_name} ${family_name}`,
                                            authResponse,
                                            generatedId: info.generatedId
                                        }
                                    }
                                ))
                            } else {
                                reject()
                            }        
                        }
                    }),
                    [FEATURES.EXPORT_TO_DROPBOX]: (info = {}) => new Promise(async (resolve, reject) => {
                        folderInfo = {};
                        let firstResp = info.firstResp;

                        if (!firstResp) {
                            firstResp = await new Promise((resolve, reject) => {
                                openConfirm(
                                    {
                                        message: jsTranslations.first_auth.replace('{STORAGE}', 'Dropbox'),
                                        message2: jsTranslations.click_confirm,
                                        title: jsTranslations.save_to_dropbox
                                    }, resolve)    
                            })
                        }

                        if (firstResp === 'yes') {
                            let authResponse = info.authResponse;

                            if (!authResponse) {
                                authResponse = await getDropboxAuthToken();
                            }
                            
                            if (authResponse && authResponse.access_token) {
                                let {
                                    familyName: family_name,
                                    givenName: given_name,
                                    email
                                } = info;

                                if (!family_name || !given_name || !email) {
                                    const res = await loadDropboxUserInfo(authResponse);

                                    family_name = res.name.surname;
                                    given_name = res.name.given_name;
                                    email = res.email;
                                }

                                await new Promise((resolve, reject) => {
                                    openConfirm(
                                        {
                                            renderComponent: () => {
                                                return (
                                                    <InfoRenderer
                                                        familyName={family_name}
                                                        givenName={given_name}
                                                        email={email}
                                                        folder={info.name || 'Files'}
                                                        jsTranslations={jsTranslations}
                                                        onChangeFolder={updateFolderInfo}
                                                        dropboxAccessToken={authResponse.access_token}
                                                        type='dropbox'
                                                    />
                                                )
                                            },
                                            title: jsTranslations.save_to_dropbox,
                                            padTitle: false
                                        }, resolve, reject);
                                })

                                resolve(formatResponse(
                                    {
                                        payload: {
                                            folder_path: folderInfo.path || null,
                                            access_token: authResponse.access_token,
                                            email
                                        },
                                        info: {
                                            title: jsTranslations.save_to_dropbox,
                                            description: folderInfo.name ? jsTranslations.saving_to_folder.replace('{FOLDER}', folderInfo.name).replace('{EMAIL}', email) : `${jsTranslations.saving_to_base}: ${email}`,
                                            name: folderInfo.name,
                                            user_name: `${given_name} ${family_name}`,
                                            email,
                                            firstResp,
                                            familyName: family_name,
                                            givenName: given_name,
                                            authResponse,
                                            generatedId: info.generatedId
                                        }
                                    }
                                ))
                            }  
                        }
                    }),
                    [FEATURES.EXTEND_EXPIRY]: () => new Promise(async (resolve) => {
                        resolve(formatResponse(
                            {
                                payload: {},
                                info: {
                                    title: jsTranslations.extend_expiry,
                                    description: jsTranslations.extend_expiry_30_days
                                }
                            }
                        ))
                    }),
                    [FEATURES.DOWNLOAD_NOTIFICATIONS]: () => new Promise(async (resolve, reject) => {
                        resolve(formatResponse(
                            {
                                payload: {},
                                info: {
                                    title: jsTranslations.download_notification,
                                    description: jsTranslations.download_notification_description
                                }
                            }
                        ))
                    })
                }}
                {...props}
            />
        );
    }
));


const formatResponse = (resp = {}) => {
    if (!resp.info) {
        resp.info = {};
    }

    if (!resp.info.generatedId) {
        /**
         * This is used to track the selected feature,
         * to enable duplicate feature selections
         */
        resp.info.generatedId = generateId();
    }

    if (!resp.info.timestamp) {
        /**
         * This helps to sort the added features
         */
        resp.info.timestamp = Date.now()
    }

    return resp;
}
export default withFeatures;